import { useState } from 'react';
import { api, ApiError, type User } from './api';
import { useMarca } from './marca';
import { Campo } from './ui';
import { PALETAS, aplicarTema, parseTema, serializarTema, type ModoTema, type Paleta } from './tema';

const MODOS: Array<{ id: ModoTema; nombre: string }> = [
  { id: 'sistema', nombre: 'Según el sistema' },
  { id: 'claro', nombre: 'Claro' },
  { id: 'oscuro', nombre: 'Oscuro' },
];

export function Perfil({ user, onUser }: { user: User; onUser: (u: User) => void }) {
  const inicial = parseTema(user.theme);
  const [displayName, setDisplayName] = useState(user.displayName);
  const [email, setEmail] = useState(user.email ?? '');
  const [modo, setModo] = useState<ModoTema>(inicial.modo);
  const marca = useMarca();
  // La paleta institucional lleva el nombre de la organización, si hay.
  const nombreDe = (p: (typeof PALETAS)[number]) => (p.id === 'marca' && marca.nombre) || p.nombre;
  const [paleta, setPaleta] = useState<Paleta>(inicial.paleta);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [avisoClave, setAvisoClave] = useState<string | null>(null);
  const [errorClave, setErrorClave] = useState<string | null>(null);

  /** El tema se aplica al instante y se persiste, para que se vea al elegirlo. */
  async function cambiarTema(nuevoModo: ModoTema, nuevaPaleta: Paleta) {
    setModo(nuevoModo);
    setPaleta(nuevaPaleta);
    aplicarTema(nuevoModo, nuevaPaleta);

    try {
      const { user: actualizado } = await api.updateProfile({
        theme: serializarTema(nuevoModo, nuevaPaleta),
      });
      onUser(actualizado);
    } catch (err) {
      // El motivo real va a la vista: un mensaje genérico acá escondió durante
      // un rato que el servidor estaba rechazando el valor.
      const detalle = err instanceof ApiError ? ` (${err.message})` : '';
      setError(`El tema se aplicó, pero no pudimos guardarlo en tu perfil${detalle}.`);
    }
  }

  async function guardarDatos() {
    setError(null);
    setAviso(null);

    try {
      const { user: actualizado } = await api.updateProfile({
        displayName: displayName.trim(),
        email: email.trim() || null,
      });
      onUser(actualizado);
      setAviso('Datos guardados.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos guardar los datos.');
    }
  }

  async function cambiarClave() {
    setErrorClave(null);
    setAvisoClave(null);

    if (nueva !== repetida) {
      setErrorClave('La nueva contraseña y su repetición no coinciden.');
      return;
    }
    if (nueva.length < 8) {
      setErrorClave('La nueva contraseña necesita al menos 8 caracteres.');
      return;
    }

    try {
      await api.changePassword(actual, nueva);
      setActual('');
      setNueva('');
      setRepetida('');
      setAvisoClave('Contraseña actualizada. Se cerraron tus otras sesiones.');
    } catch (err) {
      setErrorClave(err instanceof ApiError ? err.message : 'No pudimos cambiar la contraseña.');
    }
  }

  return (
    <section className="perfil">
      <div className="panel">
        <h2>Mis datos</h2>

        <Campo id="perfil-nombre" etiqueta="Nombre y apellido">
          <input
            id="perfil-nombre"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Campo>

        <Campo id="perfil-email" etiqueta="Correo" ayuda="Ahí llegan los avisos de archivos compartidos.">
          <input
            id="perfil-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Campo>

        <Campo id="perfil-usuario" etiqueta="Usuario" ayuda="No se puede cambiar.">
          <input id="perfil-usuario" value={user.username} disabled />
        </Campo>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {aviso && <p className="aviso-ok">{aviso}</p>}

        <button onClick={guardarDatos}>Guardar datos</button>
      </div>

      <div className="panel">
        <h2>Apariencia</h2>

        <Campo id="perfil-modo" etiqueta="Tema">
          <select
            id="perfil-modo"
            value={modo}
            onChange={(e) => cambiarTema(e.target.value as ModoTema, paleta)}
          >
            {MODOS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.nombre}
              </option>
            ))}
          </select>
        </Campo>

        <fieldset>
          <legend>Color</legend>
          <div className="paletas">
            {PALETAS.map((p) => (
              <button
                key={p.id}
                className={paleta === p.id ? 'paleta activa' : 'paleta'}
                onClick={() => cambiarTema(modo, p.id)}
                aria-pressed={paleta === p.id}
                title={nombreDe(p)}
              >
                <span className="muestra" style={{ background: p.muestra }} />
                {nombreDe(p)}
              </button>
            ))}
          </div>
        </fieldset>
      </div>

      <div className="panel">
        <h2>Cambiar contraseña</h2>

        <Campo id="clave-actual" etiqueta="Contraseña actual">
          <input
            id="clave-actual"
            type="password"
            autoComplete="current-password"
            value={actual}
            onChange={(e) => setActual(e.target.value)}
          />
        </Campo>

        <Campo id="clave-nueva" etiqueta="Nueva contraseña" ayuda="Mínimo 8 caracteres.">
          <input
            id="clave-nueva"
            type="password"
            autoComplete="new-password"
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
          />
        </Campo>

        <Campo id="clave-repetida" etiqueta="Repetir la nueva">
          <input
            id="clave-repetida"
            type="password"
            autoComplete="new-password"
            value={repetida}
            onChange={(e) => setRepetida(e.target.value)}
          />
        </Campo>

        {errorClave && (
          <p className="error" role="alert">
            {errorClave}
          </p>
        )}
        {avisoClave && <p className="aviso-ok">{avisoClave}</p>}

        <button onClick={cambiarClave} disabled={!actual || !nueva}>
          Cambiar contraseña
        </button>
      </div>
    </section>
  );
}
